import { sortBy } from 'lodash';
import { useSelector } from 'react-redux';
import { Field, formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';
import styled from 'styled-components';

import InputField from 'dashboard/components/FormControls/InputField';
import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { GOVERNMENT_ID_DISPLAY_STRINGS } from 'lib/constants/governmentIds';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';
import { isRequired, withMessage } from 'lib/utils/formValidation';

const validate = {
  identificationNumber: withMessage('Please enter an ID number', isRequired),
  countryCode: withMessage('Please select an issuing country', isRequired),
  identificationClass: withMessage('Please select an ID type', isRequired),
};

const InputGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: 16px;
`;

const ButtonGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
`;

const ISSUING_COUNTRIES_OPTIONS = sortBy(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  Object.keys(COUNTRIES).map((countryCode: CountryCode) => ({
    value: countryCode,
    label: COUNTRIES[countryCode],
  })),
  ['label'],
);

const IDENTIFICATION_CLASSES = sortBy(
  [
    ...Object.entries(GOVERNMENT_ID_DISPLAY_STRINGS).map(([value, label]) => {
      return { value, label };
    }),
    { value: 'ssn', label: 'Social Security Number' },
  ],
  ['label'],
);

const selector = formValueSelector('FormNewAccountIdentificationProperty');

interface FormNewAccountIdentificationPropertyProps {
  handleCancel: () => void;
}

const FormNewAccountIdentificationProperty = ({
  handleCancel,
  handleSubmit,
  submitting,
}: FormNewAccountIdentificationPropertyProps &
  InjectedFormProps<
    NewAccountIdentificationPropetyFormData,
    FormNewAccountIdentificationPropertyProps
  >) => {
  // For SSN, the only valid country code is US
  const identificationClass = useSelector<RootState, string>((state) =>
    selector(state, 'identification-class'),
  );
  const issuingCountriesOptions =
    identificationClass === 'ssn'
      ? [{ value: 'US', label: COUNTRIES['US'] }]
      : ISSUING_COUNTRIES_OPTIONS;

  return (
    <form onSubmit={handleSubmit}>
      <InputGrid>
        <Field
          component={ReactSelectField}
          name='identification-class'
          options={IDENTIFICATION_CLASSES}
          isMulti={false}
          validate={validate.identificationClass}
          placeholder='Select an ID Type'
        />
        <Field
          component={ReactSelectField}
          name='country-code'
          options={issuingCountriesOptions}
          isMulti={false}
          validate={validate.countryCode}
          placeholder='Select a Country'
        />
        <Field
          component={InputField}
          name='identification-number'
          type='text'
          validate={validate.identificationNumber}
          disabled={submitting}
        />
      </InputGrid>
      <HStack equal={true}>
        <div></div>
        <ButtonGrid>
          <Button type='submit' disabled={submitting} isPending={submitting} width='auto'>
            Update
          </Button>
          <Button
            width='auto'
            variant='basic'
            id='new-account-property__cancel'
            disabled={submitting}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </ButtonGrid>
      </HStack>
    </form>
  );
};

export interface NewAccountIdentificationPropetyFormData {
  'identification-number': string;
  'identification-class': string;
  'country-code': string;
}

export default reduxForm<
  NewAccountIdentificationPropetyFormData,
  FormNewAccountIdentificationPropertyProps
>({
  form: 'FormNewAccountIdentificationProperty',
})(FormNewAccountIdentificationProperty);
